import { z } from "zod";

/**
 * Typed, validated environment configuration.
 *
 * Fails fast (at import time, i.e. app boot) if a required Vite env var is
 * missing or malformed, instead of surfacing as a confusing runtime error
 * deep inside a Supabase/middleware call later.
 *
 * All of these are intentionally public — the Flutter app already bundles
 * the equivalent `.env` as a web asset, so the anon key, contract id and
 * middleware base URL are already exposed to the browser today. This is not
 * a new exposure, just the same one carried forward. See MIGRATION.md.
 */
const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  // The middleware client concatenates paths directly onto this, so it must
  // end with a trailing slash (matches the Flutter app's assumption).
  VITE_API_BASE_URL: z.string().url().refine((v) => v.endsWith("/"), {
    message: "VITE_API_BASE_URL must end with a trailing slash",
  }),
  // Multi-tenancy. With no root domain every host is the default tenant (today's single tenant).
  VITE_ROOT_DOMAIN: z.string().min(1).optional(),
  VITE_DEFAULT_TENANT_SLUG: z.string().min(1).default("gas"),
  VITE_DEV_TENANT: z.string().min(1).optional(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`)
    .join("\n");
  throw new Error(
    `Invalid environment configuration. Check your .env against .env.example:\n${issues}`,
  );
}

export const config = {
  supabaseUrl: parsed.data.VITE_SUPABASE_URL,
  supabaseAnonKey: parsed.data.VITE_SUPABASE_ANON_KEY,
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  rootDomain: parsed.data.VITE_ROOT_DOMAIN,
  defaultTenantSlug: parsed.data.VITE_DEFAULT_TENANT_SLUG,
  devTenant: parsed.data.VITE_DEV_TENANT,
} as const;
